import { Building2, TriangleAlert, Users } from "lucide-react";

import { type CarrierTeamMember, useCarrierTeam } from "@/api/carrier";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CompanyProfileForm } from "@/features/company-profile-form";
import { hasHttpStatus } from "@/lib/api-error";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

type CarrierSummary = {
  carrierId: string;
  carrierName: string;
  carrierAdminCount: number;
  dispatcherCount: number;
};

export default function CompanySettingsScreen() {
  const team = useCarrierTeam();
  const teamRows: CarrierTeamMember[] = Array.isArray(team.data)
    ? (team.data as CarrierTeamMember[])
    : (team.data?.data ?? []);
  const summaries = buildSummaries(teamRows);
  const teamForbidden = hasHttpStatus(team.error, 403);

  return (
    <div className="carrier-page">
      <div className="mx-auto max-w-4xl space-y-5">
        <PageHeader title="Company" />

        <Card className="overflow-hidden">
          <CardHeader className="border-b p-4">
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                <Building2 className="h-4 w-4" aria-hidden="true" />
              </div>
              <div>
                <CardTitle className="text-base">Company profile</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Used on labels, customer tracking, and EZX dispatch.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <CompanyProfileForm />
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="border-b p-4">
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 rounded-md border bg-muted/40 p-2">
                <Users className="h-4 w-4" aria-hidden="true" />
              </div>
              <div>
                <CardTitle className="text-base">Team summary</CardTitle>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  People with access to this carrier workspace.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {team.error && teamRows.length > 0 && !teamForbidden && (
              <div
                role="alert"
                className="mb-3 flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 sm:flex-row sm:items-center sm:justify-between"
              >
                <span>
                  Team refresh failed. Loaded membership totals remain
                  available.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm-touch"
                  className="bg-background"
                  onClick={() => void team.refetch()}
                >
                  Try again
                </Button>
              </div>
            )}

            {teamForbidden ? (
              <TeamError forbidden onRetry={() => void team.refetch()} />
            ) : team.isLoading && teamRows.length === 0 ? (
              <TeamLoading />
            ) : team.error && teamRows.length === 0 ? (
              <TeamError
                forbidden={hasHttpStatus(team.error, 403)}
                onRetry={() => void team.refetch()}
              />
            ) : summaries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No carrier memberships found for this account.
              </p>
            ) : (
              <div className="space-y-2">
                {summaries.map((summary) => (
                  <TeamSummary key={summary.carrierId} summary={summary} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function TeamSummary({ summary }: { summary: CarrierSummary }) {
  return (
    <article className="flex min-w-0 items-start gap-3 rounded-md border bg-background p-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-sm font-semibold text-primary">
        {initials(summary.carrierName)}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="break-words text-sm font-semibold">
          {summary.carrierName}
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {summary.carrierAdminCount} carrier admin
          {summary.carrierAdminCount === 1 ? "" : "s"} ·{" "}
          {summary.dispatcherCount} dispatcher
          {summary.dispatcherCount === 1 ? "" : "s"}
        </p>
        <p className="mt-1 break-all font-mono text-[10px] text-muted-foreground/70">
          Workspace {summary.carrierId}
        </p>
      </div>
    </article>
  );
}

function TeamLoading() {
  return (
    <div aria-label="Loading team summary" role="status" className="space-y-2">
      <Skeleton className="h-16" />
      <Skeleton className="h-16" />
    </div>
  );
}

function TeamError({
  forbidden,
  onRetry,
}: {
  forbidden: boolean;
  onRetry: () => void;
}) {
  return (
    <div role="alert" className="space-y-3">
      <div className="flex items-start gap-2 text-sm text-destructive">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {forbidden
            ? "You do not have permission to view the team summary."
            : "Team summary could not be loaded."}
        </span>
      </div>
      {!forbidden && (
        <Button
          type="button"
          variant="outline"
          size="sm-touch"
          onClick={onRetry}
        >
          Try again
        </Button>
      )}
    </div>
  );
}

function buildSummaries(team: CarrierTeamMember[]): CarrierSummary[] {
  const byCarrier = new Map<string, CarrierSummary>();
  for (const member of team) {
    for (const m of member.memberships) {
      const entry = byCarrier.get(m.carrierId) ?? {
        carrierId: m.carrierId,
        carrierName: m.carrier.name,
        carrierAdminCount: 0,
        dispatcherCount: 0,
      };
      if (m.role === "CARRIER_OWNER") entry.carrierAdminCount += 1;
      else if (m.role === "CARRIER_DISPATCHER") entry.dispatcherCount += 1;
      byCarrier.set(m.carrierId, entry);
    }
  }
  return Array.from(byCarrier.values());
}
